<ng-container *ngIf="layer">
    <div class="section title">
        <button
            [cdkCopyToClipboard]="layer.nameOrDefault"
            class="icon-button"
            title="Copy to clipboard"
        >
            <cds-icon shape="copy-to-clipboard"></cds-icon>
        </button>
        <div class="app-typo-h3" [title]="layer.nameOrDefault">{{ layer.nameOrDefault }}</div>
        <button (click)="close.emit()" class="icon-button icon-big close-button">
            <cds-icon shape="window-close"></cds-icon>
        </button>
    </div>

    <div class="section flex-row col-gap">
        <label class="label">Visibility</label>
        <button class="icon-button" (click)="layer.toggleVisible()">
            <ng-container *ngIf="layer.visible$ | async">
                <cds-icon shape="eye"></cds-icon>
            </ng-container>
            <ng-container *ngIf="!(layer.visible$ | async)">
                <cds-icon shape="eye-hide"></cds-icon>
            </ng-container>
        </button>
    </div>

    <ng-container *ngIf="layer.shaderColor as shaderColor">
        <div class="section flex-row col-gap">
            <label class="label">Color</label>
            <button
                class="color-button"
                ngx-colors-trigger
                [style.background-color]="shaderColor.value$ | async"
                format="hex"
                [hideTextInput]="true"
                colorPickerControls="no-alpha"
                [palette]="colorPalette"
                [ngModel]="shaderColor.value$ | async"
                (ngModelChange)="shaderColor.setValue($event)"
                overlayClassName="color-picker"
            ></button>
        </div>
    </ng-container>

    <ng-container *ngIf="layer.segmentationColor as segmentationColor">
        <div class="section">
            <label class="label">Segmentation Color</label>
            <div class="flex-row segmentation-color-content">
                <cds-radio-group layout="vertical">
                    <cds-radio>
                        <label>Random Colors</label>
                        <input
                            type="radio"
                            name="randomColors"
                            [ngModel]="segmentationColorMode(segmentationColor.value$ | async)"
                            [value]="SegmentationColorModeEnum.RandomSeed"
                            (ngModelChange)="
                                setSegmentationColorMode(SegmentationColorModeEnum.RandomSeed)
                            "
                        />
                    </cds-radio>
                    <cds-radio>
                        <label>Fixed Color</label>
                        <input
                            type="radio"
                            name="fixedColor"
                            [ngModel]="segmentationColorMode(segmentationColor.value$ | async)"
                            [value]="SegmentationColorModeEnum.FixedColor"
                            (ngModelChange)="
                                setSegmentationColorMode(SegmentationColorModeEnum.FixedColor)
                            "
                        />
                    </cds-radio>
                </cds-radio-group>
                <div *ngIf="segmentationColor.value$ | async as color">
                    <button
                        class="color-button"
                        ngx-colors-trigger
                        [style.background-color]="color"
                        format="hex"
                        [hideTextInput]="true"
                        colorPickerControls="no-alpha"
                        [palette]="colorPalette"
                        [ngModel]="color"
                        (ngModelChange)="segmentationColor.setValue($event)"
                        overlayClassName="color-picker"
                    ></button>
                </div>
            </div>
        </div>
    </ng-container>

    <div *ngIf="hasSliderControls" class="section flex-col">
        <form *ngIf="layer.opacityPercent as opacityPercent">
            <cds-form-group>
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Opacity</label>
                        <input
                            clrRange
                            name="opacity-range"
                            type="range"
                            max="100"
                            min="0"
                            [ngModel]="opacityPercent.value$ | async"
                            (ngModelChange)="opacityPercent.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="opacity-number"
                        type="number"
                        max="100"
                        min="0"
                        [ngModel]="opacityPercent.value$ | async"
                        (ngModelChange)="opacityPercent.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>

        <form *ngIf="layer.opacityUnselectedPercent as opacityUnselectedPercent">
            <cds-form-group layout="vertical">
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Opacity (Not selected)</label>
                        <input
                            clrRange
                            name="opacity-range"
                            type="range"
                            max="100"
                            min="0"
                            [ngModel]="opacityUnselectedPercent.value$ | async"
                            (ngModelChange)="opacityUnselectedPercent.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="opacity-number"
                        type="number"
                        max="100"
                        min="0"
                        [ngModel]="opacityUnselectedPercent.value$ | async"
                        (ngModelChange)="opacityUnselectedPercent.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>

        <form *ngIf="layer.saturationPercent as saturationPercent">
            <cds-form-group layout="vertical">
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Saturation</label>
                        <input
                            clrRange
                            name="saturation-range"
                            type="range"
                            max="100"
                            min="0"
                            [ngModel]="saturationPercent.value$ | async"
                            (ngModelChange)="saturationPercent.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="saturation-number"
                        type="number"
                        max="100"
                        min="0"
                        [ngModel]="saturationPercent.value$ | async"
                        (ngModelChange)="saturationPercent.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>

        <form *ngIf="layer.brightness as brightness">
            <cds-form-group layout="vertical">
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Brightness</label>
                        <input
                            clrRange
                            name="brightness-range"
                            type="range"
                            max="1"
                            min="-1"
                            step="0.01"
                            [ngModel]="brightness.value$ | async"
                            (ngModelChange)="brightness.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="brightness-number"
                        type="number"
                        max="1"
                        min="-1"
                        step="0.01"
                        [ngModel]="brightness.value$ | async"
                        (ngModelChange)="brightness.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>

        <form *ngIf="layer.contrast as contrast">
            <cds-form-group layout="vertical">
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Contrast</label>
                        <input
                            clrRange
                            name="contrast-range"
                            type="range"
                            max="3"
                            min="-3"
                            step="0.01"
                            [ngModel]="contrast.value$ | async"
                            (ngModelChange)="contrast.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="contrast-number"
                        type="number"
                        max="3"
                        min="-3"
                        step="0.01"
                        [ngModel]="contrast.value$ | async"
                        (ngModelChange)="contrast.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>
    </div>
</ng-container>
